import type {
  Report,
  ReportDetail,
  ReportListItem,
  ReportRejectionReason,
  ReportStatus,
} from '@beacon/core';

import type { BeaconClient } from '../client.js';

export interface ReportListResponse {
  reports: ReportListItem[];
}

export interface ListReportsParams {
  /** Filter by one or more statuses. Defaults to all when omitted. */
  statuses?: ReadonlyArray<ReportStatus>;
  /** Filter by period month, e.g. '2026-04'. */
  period?: string;
}

export const reportsApi = {
  list(
    client: BeaconClient,
    params: ListReportsParams = {}
  ): Promise<ReportListResponse> {
    const parts: string[] = [];
    if (params.statuses && params.statuses.length > 0) {
      parts.push(`status=${encodeURIComponent(params.statuses.join(','))}`);
    }
    if (params.period) {
      parts.push(`period=${encodeURIComponent(params.period)}`);
    }
    const qp = parts.length > 0 ? `?${parts.join('&')}` : '';
    return client.get<ReportListResponse>(`/api/reports${qp}`);
  },

  getById(client: BeaconClient, id: string): Promise<ReportDetail> {
    return client.get<ReportDetail>(`/api/reports/${encodeURIComponent(id)}`);
  },

  /**
   * Transition a report from 'review' (or back from 'approved') to
   * 'approved'. PDF generation + client send are deferred to async
   * downstream workers triggered by this status change.
   */
  approve(client: BeaconClient, id: string): Promise<Report> {
    return client.post<Report>(
      `/api/reports/${encodeURIComponent(id)}/approve`
    );
  },

  /**
   * Send the approved report to the supplied list of email addresses.
   * Returns the report row with status='sent' and the recipient list
   * stamped on `sentToEmails`.
   */
  send(
    client: BeaconClient,
    id: string,
    recipients: ReadonlyArray<string>
  ): Promise<Report> {
    return client.post<Report>(
      `/api/reports/${encodeURIComponent(id)}/send`,
      { recipients }
    );
  },

  /**
   * Send the report back to the delivery team for changes. Flips status
   * `review` → `draft` and stamps the rejection reason + free-form note
   * onto the report row for the audit trail and the Draft-tab queue.
   */
  updateCoverNotes(
    client: BeaconClient,
    id: string,
    coverNotes: string
  ): Promise<Report> {
    return client.patch<Report>(
      `/api/reports/${encodeURIComponent(id)}/cover-notes`,
      { coverNotes }
    );
  },

  reject(
    client: BeaconClient,
    id: string,
    input: { reason: ReportRejectionReason; note: string }
  ): Promise<Report> {
    return client.post<Report>(
      `/api/reports/${encodeURIComponent(id)}/reject`,
      input
    );
  },
};
